import React, { createContext, useContext, useState, useEffect } from 'react';
import { apiConfig } from "../utils/apiConfig";
import axios from "axios";
import logger from '../utils/logger';
import { updateHoldingsData } from '../utils/apiConfig';
import { clearDemoStorage } from './DemoContext';
import { clearDemoScheduleLocal } from '../utils/clearDemoScheduleLocal';

const AuthContext = createContext();
const AUTH_STORAGE_KEY = 'auth_state';

export function AuthProvider({ children }) {
  const [state, setState] = useState({
    user: null,
    token: null,
    isLoading: true,
    error: null
  });

  // On mount, try to restore auth state from storage
  useEffect(() => {
    try {
      const stored = localStorage.getItem(AUTH_STORAGE_KEY);
      if (stored) {
        const parsed = JSON.parse(stored);
        logger.info('🔐 Restored auth state from storage');
        setState(prev => ({
          ...prev,
          user: parsed.user,
          token: parsed.token,
          isLoading: false
        }));
      } else {
        setState(prev => ({ ...prev, isLoading: false }));
      }
    } catch (error) {
      logger.error('🔐 Failed to restore auth state:', error);
      setState(prev => ({ ...prev, isLoading: false, error }));
    }
  }, []);

  // Persist auth state changes to storage
  useEffect(() => {
    if (!state.isLoading) {
      try {
        if (state.token && state.user) {
          localStorage.setItem(AUTH_STORAGE_KEY, JSON.stringify({
            token: state.token,
            user: state.user
          }));
        } else {
          localStorage.removeItem(AUTH_STORAGE_KEY);
        }
      } catch (error) {
        logger.error('🔐 Failed to persist auth state:', error);
      }
    }
  }, [state.token, state.user, state.isLoading]);

  const login = async (loginData) => {
    try {
      // Extract data from loginData
      const { token, refreshToken, user } = loginData;

      // Store tokens in localStorage
      localStorage.setItem('token', token);
      localStorage.setItem('refreshToken', refreshToken);

      // Update state
      setState(prev => ({
        ...prev,
        user,
        token,
        error: null
      }));

      // Update holdings data in the background
      try {
        await updateHoldingsData();
        logger.info('📊 Holdings data updated successfully');
      } catch (error) {
        logger.error('📊 Failed to update holdings data:', error);
        // Don't throw this error as it shouldn't block login
      }

      logger.info('🔐 Login successful:', { user });
      return { user, token };
    } catch (error) {
      logger.error('🔐 Login error:', error);
      setState(prev => ({ ...prev, error }));
      throw error;
    }
  };

  const logout = () => {
    // Clear demo state first (matches STOP_DEMO reset)
    clearDemoStorage();
    clearDemoScheduleLocal?.();

    setState({
      user: null,
      token: null,
      isLoading: false,
      error: null
    });
    localStorage.removeItem(AUTH_STORAGE_KEY);
  };

  return (
    <AuthContext.Provider 
      value={{
        user: state.user,
        token: state.token,
        isLoading: state.isLoading,
        error: state.error,
        login,
        logout
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
